// src/components/OperationsTable.tsx - REFACTORED VERSION
import React, { useState, useEffect } from 'react';
import { Button, Space, Modal, message, Tag, InputNumber, Form, Typography, Tooltip, Avatar } from 'antd';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import {
  EditOutlined,
  FileOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  UserOutlined,
  DownOutlined,
  UpOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { API_BASE_URL } from '../../../constants/config';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { TFunction } from 'i18next';
import { useEffectiveAdminId, useAdminSelector } from '../../../contexts/AdminSelectorContext';
import { useOutletContext } from 'react-router-dom';
import { GlassTable } from '../../../shared/components/GlassTable/GlassTable';
import { OperationTypeIcon, getOperationTypeColor, getOperationTypeLabel, type OperationType } from './OperationTypeIcon';
import { OperationFilters, type OperationFilterValues } from './OperationFilters';
import { OperationExpandedRow } from './OperationExpandedRow';

const { Text } = Typography;

interface User {
  id: string;
  username: string;
  accountName?: string;
}

interface SearchParams {
  username: string;
  type?: OperationType;
  linkedTransferId: string;
}

interface Operation {
  id: string;
  operationNumber?: string;
  type: OperationType;
  userId: string;
  adminId: string;
  amount: number | null;
  bonus: number;
  receipt?: string;
  status: string;
  message?: string;
  createdAt: Date;
  user: User;
  admin?: User;
  linkedTransferId: number | null;
  executionTime: number;
  requestData?: any;
}

interface PaginationParams {
  current: number;
  pageSize: number;
  total?: number;
}

interface OperationsResponse {
  data: Operation[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface EditFormProps {
  initialValues: { amount: number; bonus: number };
  onSubmit: (values: { amount: number; bonus: number }) => Promise<void>;
  t: TFunction;
}

export interface OperationsSearchContext {
  username: string;
  type?: OperationType;
  linkedTransferId: string;
  onUsernameChange: (value: string) => void;
  onTypeChange: (value: OperationType | undefined) => void;
  onLinkedTransferIdChange: (value: string) => void;
}

const EditForm: React.FC<EditFormProps> = ({ initialValues, onSubmit, t }) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Form.Item
        name="amount"
        label={t('common.amount')}
        rules={[{ required: true, message: t('operations.inputAmount') }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value: string | undefined): number => {
            const parsed = parseFloat((value || '').replace(/\$\s?|(,*)/g, ''));
            return isNaN(parsed) ? 0 : parsed;
          }}
          min={0}
          precision={2}
        />
      </Form.Item>

      <Form.Item
        name="bonus"
        label={t('common.bonus')}
        rules={[{ required: true, message: t('operations.inputBonus') }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value: string | undefined): number => {
            const parsed = parseFloat((value || '').replace(/\$\s?|(,*)/g, ''));
            return isNaN(parsed) ? 0 : parsed;
          }}
          min={0}
          precision={2}
        />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
          {t('operations.updateOperation')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export const OperationsTable: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isReceiptModalVisible, setIsReceiptModalVisible] = useState<boolean>(false);
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    username: '',
    type: undefined,
    linkedTransferId: '',
  });
  const [pagination, setPagination] = useState<PaginationParams>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // Get effective admin ID for filtering (respects SuperAdmin admin selector)
  const effectiveAdminId = useEffectiveAdminId();
  const { isSuperAdmin } = useAdminSelector();

  // Get search context from outlet context - parent controls the search state
  const context = useOutletContext<{
    operationsSearchParams?: SearchParams;
  }>();

  const queryClient = useQueryClient();

  // Use search params from parent context if available, otherwise use local state
  const effectiveSearchParams = context?.operationsSearchParams || searchParams;

  // Debug logging
  useEffect(() => {
  }, [context?.operationsSearchParams, searchParams, effectiveSearchParams]);

  // Sync local state when parent context changes
  useEffect(() => {
    if (context?.operationsSearchParams) {
      setSearchParams(context.operationsSearchParams);
    }
  }, [context?.operationsSearchParams]);

  // Function to fetch operations with pagination and filtering
  const fetchOperations = async ({
    page = 1,
    pageSize = 10,
    username = '',
    type = undefined,
    linkedTransferId = '',
    adminId = undefined,
    filters = {}
  }: {
    page?: number;
    pageSize?: number;
    username?: string;
    type?: OperationType;
    linkedTransferId?: string;
    adminId?: string | null;
    filters?: OperationFilterValues;
  }): Promise<OperationsResponse> => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('No token found');
      return {
        data: [],
        total: 0,
        page,
        limit: pageSize,
        hasMore: false
      };
    }

    const params: Record<string, any> = {
      page,
      limit: pageSize,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    };

    // Only include adminId if it's not null (null = SuperAdmin viewing all)
    if (adminId !== null && adminId !== undefined) {
      params.adminId = adminId;
    }

    if (username) params.username = username;
    if (type) params.type = type;
    if (linkedTransferId) params.linkedTransferId = linkedTransferId;

    // Apply advanced filters
    if (filters.type && filters.type.length > 0) {
      params.types = filters.type.join(',');
    }
    if (filters.status && filters.status.length > 0) {
      params.statuses = filters.status.join(',');
    }
    if (filters.username) {
      params.username = filters.username;
    }
    if (filters.dateRange && filters.dateRange.length === 2) {
      params.startDate = filters.dateRange[0].startOf('day').toISOString();
      params.endDate = filters.dateRange[1].endOf('day').toISOString();
    }
    if (filters.amountMin !== undefined) {
      params.amountMin = filters.amountMin;
    }
    if (filters.amountMax !== undefined) {
      params.amountMax = filters.amountMax;
    }
    if (filters.linkedTransferId) {
      params.linkedTransferId = filters.linkedTransferId;
    }


    const response = await axios.get<OperationsResponse>(`${API_BASE_URL}/api/operations`, {
      headers: {
        Authorization: `Bearer ${token}`,
        // Only include x-admin-id header if not null
        ...(adminId !== null && adminId !== undefined && { 'x-admin-id': adminId })
      },
      params
    });

    return response.data;
  };

  // Filter state
  const [activeFilters, setActiveFilters] = useState<OperationFilterValues>({});

  // Optimized query with pagination support
  const {
    data,
    isLoading,
    isFetching,
    isPlaceholderData
  } = useQuery<OperationsResponse>({
    queryKey: [
      'operations',
      pagination.current,
      pagination.pageSize,
      effectiveSearchParams.username,
      effectiveSearchParams.type,
      effectiveSearchParams.linkedTransferId,
      effectiveAdminId,
      activeFilters
    ],
    queryFn: () => fetchOperations({
      page: pagination.current,
      pageSize: pagination.pageSize,
      username: effectiveSearchParams.username,
      type: effectiveSearchParams.type,
      linkedTransferId: effectiveSearchParams.linkedTransferId,
      adminId: effectiveAdminId,
      filters: activeFilters
    }),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });

  // Prefetch next page when close to the current page
  useEffect(() => {
    if (!isPlaceholderData && data?.hasMore) {
      queryClient.prefetchQuery({
        queryKey: [
          'operations',
          pagination.current + 1,
          pagination.pageSize,
          effectiveSearchParams.username,
          effectiveSearchParams.type,
          effectiveSearchParams.linkedTransferId,
          effectiveAdminId,
          activeFilters
        ],
        queryFn: () => fetchOperations({
          page: pagination.current + 1,
          pageSize: pagination.pageSize,
          username: effectiveSearchParams.username,
          type: effectiveSearchParams.type,
          linkedTransferId: effectiveSearchParams.linkedTransferId,
          adminId: effectiveAdminId,
          filters: activeFilters
        }),
      });
    }
  }, [data, isPlaceholderData, pagination.current, pagination.pageSize, queryClient, effectiveSearchParams, effectiveAdminId, activeFilters]);

  // Update total when data changes
  useEffect(() => {
    if (data?.total !== undefined) {
      setPagination(prev => ({
        ...prev,
        total: data.total
      }));
    }
  }, [data?.total]);

  const updateMutation = useMutation({
    mutationFn: (variables: { id: string; data: { amount: number; bonus: number } }) =>
      axios.patch(
        `${API_BASE_URL}/api/operations/${variables.id}`,
        variables.data,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      message.success('Operation updated successfully');
      setIsEditModalVisible(false);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update operation');
    }
  });

  // Reset to first page when search params or filters change
  useEffect(() => {
    setPagination(p => ({ ...p, current: 1 }));
  }, [effectiveSearchParams, activeFilters]);

  const handleTableChange = (newPagination: any) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleViewReceipt = async (receipt: string) => {
    if (!receipt) {
      message.error(t('operations.noReceipt'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      message.error(t('common.sessionExpired'));
      return;
    }

    setSelectedReceipt(receipt);
    setIsReceiptModalVisible(true);
  };

  const handleFilterChange = (filters: OperationFilterValues) => {
    setActiveFilters(filters);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  // Define columns
  const columns: any[] = [
    {
      title: () => (
        <Space size="small">
          <FilterOutlined style={{ fontSize: 12 }} />
          <span>Tipo</span>
        </Space>
      ),
      dataIndex: 'type',
      key: 'type',
      width: 80,
      fixed: isMobile ? undefined : 'left',
      render: (type: OperationType) => (
        <Tooltip title={getOperationTypeLabel(type)}>
          <OperationTypeIcon type={type} size="medium" />
        </Tooltip>
      ),
    },
    {
      title: t('common.user'),
      dataIndex: ['user', 'username'],
      key: 'username',
      width: 140,
      render: (_: any, record: Operation) => (
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <Tag color="blue">{record.user?.username || '-'}</Tag>
        </Space>
      ),
    },
  ];

  // Only show Admin column for SuperAdmins
  if (isSuperAdmin) {
    columns.push({
      title: 'Admin',
      key: 'admin',
      width: 140,
      render: (_: any, record: Operation) => (
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }} />
          <Tag color="purple">{(record as any).admin?.username || 'N/A'}</Tag>
        </Space>
      ),
    });
  }

  columns.push(
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount: number | null, record: Operation) => (
        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
          <Text strong style={{
            fontSize: 16,
            background: `linear-gradient(135deg, ${getOperationTypeColor(record.type)}99 0%, ${getOperationTypeColor(record.type)}66 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            ${(amount ?? 0).toFixed(2)}
          </Text>
          {record.bonus > 0 && (
            <Tag color="cyan" style={{ fontSize: 10 }}>
              +${record.bonus.toFixed(2)} bonus
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('common.transfer'),
      key: 'linkedTransfer',
      width: 100,
      render: (_: any, record: Operation) => {
        const transferId = record.linkedTransferId;
        return transferId ? (
          <Tag icon={<LinkOutlined />} color="blue">
            #{transferId}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: t('common.receipt'),
      key: 'receipt',
      width: 90,
      align: 'center',
      render: (_: any, record: Operation) => (
        record.receipt ? (
          <Button
            type="link"
            icon={<FileOutlined />}
            onClick={() => handleViewReceipt(record.receipt!)}
            size="small"
          />
        ) : <Text type="secondary">-</Text>
      ),
    },
    {
      title: t('common.created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            {new Date(date).toLocaleDateString('es-ES')}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {new Date(date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </Text>
        </Space>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_: any, record: Operation) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={() => {
            setSelectedOperation(record);
            setIsEditModalVisible(true);
          }}
        />
      ),
    }
  );

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      {/* Filters */}
      <OperationFilters
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        activeFilters={activeFilters}
      />

      {/* Table */}
      <GlassTable
        columns={columns}
        dataSource={data?.data || []}
        loading={isLoading}
        rowKey="id"
        size="small"
        scroll={{ x: isMobile ? 700 : 900 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} operaciones`,
          showQuickJumper: true,
          onChange: (page) => setPagination(p => ({ ...p, current: page })),
          onShowSizeChange: (_, size) => setPagination(p => ({ ...p, pageSize: size, current: 1 }))
        }}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (record) => <OperationExpandedRow record={record} />,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.id]);
            } else {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
            }
          },
          expandIcon: ({ expanded, onExpand, record }) => (
            expanded ?
              <UpOutlined onClick={(e) => onExpand(record, e)} style={{ cursor: 'pointer', color: '#1890ff' }} /> :
              <DownOutlined onClick={(e) => onExpand(record, e)} style={{ cursor: 'pointer' }} />
          )
        }}
      />

      {/* Loading indicator */}
      {isFetching && !isLoading && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          <Space>
            <ClockCircleOutlined spin /> Cargando...
          </Space>
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        title="Editar Operación"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        destroyOnClose
        width={isMobile ? '95%' : 400}
      >
        {selectedOperation && (
          <EditForm
            initialValues={{
              amount: selectedOperation.amount || 0,
              bonus: selectedOperation.bonus
            }}
            onSubmit={async (values) => {
              await updateMutation.mutateAsync({
                id: selectedOperation.id,
                data: values
              });
            }}
            t={t}
          />
        )}
      </Modal>

      {/* Receipt Modal - Improved version */}
      {isReceiptModalVisible && selectedReceipt && (
        <Modal
          open={isReceiptModalVisible}
          onCancel={() => {
            setIsReceiptModalVisible(false);
            setSelectedReceipt(null);
          }}
          afterClose={() => {
            setSelectedReceipt(null);
          }}
          footer={null}
          destroyOnClose={true}
          width={isMobile ? '95%' : '80%'}
          bodyStyle={{
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#000',
            minHeight: isMobile ? '50vh' : '60vh',
            maxHeight: isMobile ? '70vh' : '80vh'
          }}
        >
          <ImprovedReceiptView receipt={selectedReceipt} />
        </Modal>
      )}
    </div>
  );
};

// Improved Receipt View Component
interface ImprovedReceiptViewProps {
  receipt: string;
}

const ImprovedReceiptView: React.FC<ImprovedReceiptViewProps> = ({ receipt }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [isExternal, setIsExternal] = useState<boolean>(false);

  useEffect(() => {
    loadImage();

    return () => {
      if (imageUrl && !isExternal) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [receipt]);

  const loadImage = async () => {
    setLoading(true);
    setError(false);

    try {
      let cleanPath = receipt;
      if (cleanPath.startsWith('/uploads/')) {
        cleanPath = cleanPath.substring(9);
      } else if (cleanPath.startsWith('uploads/')) {
        cleanPath = cleanPath.substring(8);
      }

      // Check if it's an external URL
      if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
        setIsExternal(true);
        setImageUrl(cleanPath);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token available');

      const apiUrl = `${API_BASE_URL}/api/panel/uploads/${cleanPath}`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(response.data);
      setImageUrl(blobUrl);
      setLoading(false);
    } catch (err) {
      console.error('Error loading receipt:', err);
      setError(true);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', padding: 40, color: 'white' }}>
        <ClockCircleOutlined spin style={{ fontSize: '48px', marginBottom: '16px' }} />
        <Text style={{ color: 'white' }}>Cargando comprobante...</Text>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'white', width: '100%' }}>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>😕</div>
        <Text style={{ fontSize: '18px', marginBottom: '16px', color: 'white', display: 'block' }}>
          Error al cargar el comprobante
        </Text>
        {isExternal && (
          <div style={{ marginTop: 16 }}>
            <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, display: 'block', marginBottom: 12 }}>
              Este comprobante está en un servidor externo y puede tener restricciones CORS.
            </Text>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => window.open(receipt, '_blank')}
            >
              Abrir en nueva pestaña
            </Button>
          </div>
        )}
        <Button
          style={{ marginTop: 16 }}
          onClick={loadImage}
        >
          Reintentar
        </Button>
      </div>
    );
  }

  if (isExternal) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'white', width: '100%' }}>
        <div style={{ fontSize: '48px', marginBottom: '24px' }}>🌐</div>
        <Text style={{ fontSize: '18px', marginBottom: '16px', color: 'white', display: 'block' }}>
          Comprobante externo detectado
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, display: 'block', marginBottom: 16 }}>
          Este comprobante está alojado en un servidor externo. Por razones de seguridad CORS,
          debes abrirlo en una nueva pestaña.
        </Text>
        <Button
          type="primary"
          size="large"
          icon={<LinkOutlined />}
          onClick={() => window.open(imageUrl, '_blank')}
        >
          Abrir Comprobante en Nueva Pestaña
        </Button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
      <img
        src={imageUrl}
        alt="Operation Receipt"
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </div>
  );
};
